<template>
  <div class="AiExpansionPanel">
    <button
      class="AiExpansionPanel-headerButtonWrapper"
      :aria-expanded="expanded"
      :aria-controls="controlsId"
      @click="toggleExpand">
      <div class="AiExpansionPanel-header">
        <ai-typo
          as="h3"
          variant="heading-03"
          class="AiExpansionPanel-headerTitle">
          {{ title }}
        </ai-typo>

        <ai-icon
          :size="24"
          name="chevron-down"
          class="AiExpansionPanel-header-icon AiExpansionPanel-header-icon--mobile"
          :class="{ 'AiExpansionPanel-header-icon--expanded': expanded }" />
        <ai-icon
          :size="36"
          name="chevron-down"
          class="AiExpansionPanel-header-icon AiExpansionPanel-header-icon--desktop"
          :class="{ 'AiExpansionPanel-header-icon--expanded': expanded }" />
      </div>
    </button>

    <div
      :id="controlsId"
      class="AiExpansionPanel-content"
      :class="{ 'AiExpansionPanel-content--is-expanded': expanded }"
      :style="{ maxHeight: `${animatedContentContainerMaxHeight}px` }">
      <div ref="animatedContent" class="AiExpansionPanel-animatedContent">
        <slot />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
type Props = {
  controlsId: string;
  title: string;
};

defineProps<Props>();

const expanded = ref(false);
const animatedContent = ref<HTMLDivElement | null>(null);
const animatedContentMaxHeight = ref(0);

onMounted(() => {
  animatedContentMaxHeight.value = animatedContent?.value?.clientHeight ?? 0;
});

const animatedContentContainerMaxHeight = computed(() =>
  expanded.value ? animatedContentMaxHeight.value : 0,
);

const toggleExpand = () => (expanded.value = !expanded.value);
</script>

<style lang="scss" scoped>
@use '@/assets/styles/utilities/constants';
@use '@/assets/styles/utilities/colors';
@use '@/assets/styles/utilities/mq';

.AiExpansionPanel-headerButtonWrapper {
  width: 100%;
}
.AiExpansionPanel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.AiExpansionPanel-headerTitle {
  text-align: left;
}

.AiExpansionPanel-header-icon {
  display: inline-block;
  color: colors.$gold-900;
  transition: all 200ms;

  &--expanded {
    transform: rotate(180deg);
  }
}

.AiExpansionPanel-header-icon--mobile {
  @media (mq.$from-medium) {
    display: none;
  }
}

.AiExpansionPanel-header-icon--desktop {
  display: none;

  @media (mq.$from-medium) {
    display: inline;
  }
}

.AiExpansionPanel-content {
  transition:
    max-height 0.3s ease-out,
    visibility 0.3s ease-in-out;
  overflow: hidden;
  visibility: hidden;

  &--is-expanded {
    visibility: visible;
  }
}

.AiExpansionPanel-animatedContent {
  padding: constants.$padding-01 0;

  @media (mq.$from-medium) {
    padding: constants.$padding-02;
  }
}
</style>
